import React, { useMemo } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { ExtractRestorableEntry, UndoRedoV2 } from "@iventis/utilities/src/undo-redo/undo-redo-v2";
import { UndoRedoContext } from "@iventis/utilities/src/undo-redo/undo-redo-v2-context";
import { isValidUuid, useConstant, useObservableCallback, useObservableValue, useRefEffectValue } from "@iventis/utilities";
import { TreeBrowserXState } from "@iventis/tree-browser";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { mapObjectUpdateToCompositionMapObject, mapObjectUpdateToLocalGeoJsonObject, objectUpdateToNode } from "@iventis/map-engine/src/utilities/converters";
import { isCompositionValid, machineEventTypes, MapMode } from "@iventis/map-engine";
import { feature, featureCollection, polygon } from "@turf/helpers";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { useSelector } from "react-redux";
import { CompositionUndoRedo } from "@iventis/map-engine/src/utilities/composition-undo-redo";
import { checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { isAnalysisLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import GeoJSON from "geojson";
import { LocalGeoJson, SelectedMapObject } from "@iventis/map-types";
import booleanWithin from "@turf/boolean-within";
import { RoutePlannerEvent, routePlannerEventStream } from "@iventis/route-planner/src/route-planner-event-stream";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { dispatch, RootState, rootStore } from "./state/root.store";
import { deleteObjects, patchMapObjectsDataFieldValues, restoreObjects, uploadGeometry } from "./state/map.slice.thunks";
import { eventStream } from "./event-stream";
import { map } from "./state/map.slice";
import {
    createMapObjectsWithSystemDataFields,
    localGeoJsonObjectToObjectUpdate,
    updateInfoForRouteObjects,
    updateMapObjectsWithSitemap,
    updateMapObjectSystemDataFields,
    useMapKeyShortcuts,
} from "./map-container.helpers";
import { getWhat3WordsValueForObjectUpdates, patchWhat3WordsValueToNullRemote } from "./map-container-what3words";
import {
    MapUndoRedoContext,
    MapUndoRedoContextType,
    MapUndoRedoItem,
    MapUndoRedoItemPartial,
    MapObjectUndoRedoEntry,
    MapObjectSelectionUndoRedoEntry,
    PartialObjectUpdate,
} from "./map-undo-redo-context";
import { mapUndoRedoEventStream } from "./map-undo-redo-stream";
import { FROM_MAP_UNDO_REDO } from "./map-constants";
import { panToObject } from "./map-object-selection-helpers";

/** Use this to wrap around the map root to make undo/redo operations available. Undo/redo side-effects are handled inside here */
export const MapUndoRedoWrapper = ({
    treeBrowserContext,
    getCompositionUndoRedo,
    children,
}: {
    treeBrowserContext: TreeBrowserXState<Node>;
    getCompositionUndoRedo: (() => CompositionUndoRedo) | undefined;
    children: React.ReactNode;
}) => {
    const { _geoJson, _mode, _bounds, mapId } = useSelector((state: RootState) => ({
        _geoJson: state.mapReducer.mapModule.geoJSON.value,
        _mode: state.mapReducer.mapModule.mode,
        _bounds: state.mapReducer.mapModule.bounds.value,
        mapId: state.mapReducer.mapModule.mapId,
    }));

    const geoJson = useRefEffectValue(_geoJson);
    const bounds = useRefEffectValue(_bounds);
    const mode = useRefEffectValue(_mode.value);

    // RULE: YOU CANNOT ENTER COMPOSITION FROM AN UNDO/REDO OPERATION

    const mapUndoRedo: Omit<MapUndoRedoContextType, "canUndo" | "canRedo"> = useConstant(() => {
        const undoRedo = new UndoRedoV2<MapUndoRedoItemPartial, MapUndoRedoItem, MapObjectUndoRedoEntry | MapObjectSelectionUndoRedoEntry>("map-undo-redo")
            // If an undo/redo leads to an update, we need to handle that here.
            // Expect the "to" property to have new state of the object
            .onUpdate((partialUpdates) => {
                if (partialUpdates.type === "mapObjectSelection") {
                    // If there are any selections, we need to update the selection state
                    const selectedMapObjectIds: SelectedMapObject[] = [];
                    const features: GeoJSON.Feature[] = [];
                    partialUpdates.change.forEach(({ to }) => {
                        to.forEach((selectedMapObject) => {
                            selectedMapObjectIds.push(selectedMapObject);
                            const feature = geoJson.current[selectedMapObject.layerId]?.find((x) => x.objectId === selectedMapObject.objectId)?.feature;
                            if (feature) {
                                features.push(feature);
                            }
                        });
                    });
                    // If the result is to select something which isn't in view, make sure we pan to the object
                    const requirePan = features.some((f) => !booleanWithin(f, polygon(bounds.current.bounds)));
                    if (requirePan) {
                        panToObject(featureCollection(features), dispatch, map);
                    }
                    eventStream.next({
                        type: machineEventTypes.selectObjects,
                        payload: { selectedMapObjectIds, from: FROM_MAP_UNDO_REDO, updateStore: true },
                    });
                }

                if (partialUpdates.type === "mapObject") {
                    // Global map state
                    let currentState = rootStore.getState().mapReducer;

                    // A mapping of layer id to layer
                    const layers = currentState.mapModule.layers.value.reduce<{ [layerId: string]: DomainLayer }>((acc, layer) => ({ ...acc, [layer.id]: layer }), {});

                    // A mapping of layer id to its' partial object updates
                    const mapObjectPartialUpdates: { [layerId: string]: { [objectId: string]: PartialObjectUpdate } } = {};
                    partialUpdates.change.forEach((update) => {
                        const { layerId, objectId } = update.to;
                        mapObjectPartialUpdates[layerId] =
                            mapObjectPartialUpdates[layerId] == null ? { [objectId]: update.to } : { ...mapObjectPartialUpdates[layerId], [objectId]: update.to };
                    });

                    // Map object updates
                    if (Object.values(mapObjectPartialUpdates).length > 0) {
                        // List of all updated objects in their complete form
                        let updatedMapObjects: ObjectUpdate[] = Object.entries(mapObjectPartialUpdates).flatMap(([layerId, objects]) => {
                            const layer = layers[layerId];
                            if (!layer) {
                                throw new Error(`Layer not found while performing undo/redo: ${layerId}`);
                            }
                            return Object.entries(objects).map(([objectId, object]) => {
                                const existingObject = currentState.mapModule.geoJSON.value[layerId]?.find((feature) => feature?.objectId === objectId);
                                // This will override any properties which have been populated in the undo/redo update
                                return {
                                    ...localGeoJsonObjectToObjectUpdate(existingObject),
                                    ...object,
                                    geometry: { ...existingObject.feature.geometry, ...object.geometry },
                                    properties: { ...existingObject.feature.properties, ...object.properties },
                                };
                            });
                        });

                        const updateMapObjectSystemDataFieldsResponse = updateMapObjectSystemDataFields(
                            currentState.mapModule.projectDataFields,
                            updatedMapObjects,
                            currentState.mapModule.layers.value
                        );
                        updatedMapObjects = updateMapObjectSystemDataFieldsResponse.updatedMapObjects;

                        // Find the sitemap for the map objects, ensure it is done before the network request to update the geometry
                        updatedMapObjects = updateMapObjectsWithSitemap(updatedMapObjects, currentState);

                        // If we updated object in question is a route, send the updated waypoints to the route controls machine
                        if (updatedMapObjects?.length === 1 && updatedMapObjects[0].properties?.modeOfTransport) {
                            // Update the map engine route controls
                            eventStream.next({
                                type: machineEventTypes.updateRoute,
                                payload: { waypoints: updatedMapObjects[0].waypoints, modeOfTransport: updatedMapObjects[0].properties?.modeOfTransport, updateStore: false },
                            });

                            // Update the route planner ui
                            routePlannerEventStream.next({
                                type: RoutePlannerEvent.SetRouteWaypoints,
                                payload: { waypoints: updatedMapObjects[0].waypoints?.map((w, i) => ({ ...w, order: i + 1 })) },
                            });
                            routePlannerEventStream.next({
                                type: RoutePlannerEvent.SetModeOfTransport,
                                payload: { modeOfTransport: updatedMapObjects[0].properties.modeOfTransport },
                            });
                            updateInfoForRouteObjects(updatedMapObjects, currentState.mapModule.projectDataFields);
                        } else {
                            // Else, send the updated geometry to the drawing machine.
                            eventStream.next({
                                type: machineEventTypes.updateDrawingGeometry,
                                payload: { features: updatedMapObjects.map((object) => mapObjectUpdateToCompositionMapObject(object).geojson), updateStore: false }, // Do not update the store since that would cause another push to undo/ redo.
                            });
                        }

                        // Update local geojson first
                        dispatch(
                            map.actions.setLocalGeoJson(
                                updatedMapObjects.map((object) => ({
                                    layerId: object.layerId,
                                    objectId: object.objectId,
                                    geojson: feature(object.geometry, object.properties),
                                    waypoints: object.waypoints,
                                }))
                            )
                        );

                        // Ensure the objects being updated are selected
                        const { objectNodes, analysisObjects } = updatedMapObjects.reduce<{ objectNodes: Node[]; analysisObjects: ObjectUpdate[] }>(
                            (acc, object) => {
                                const existsOnServer = isValidUuid(object.layerId) && currentState.mapModule.localToRemoteMapObjectIdsMap.value[object.objectId] !== undefined;
                                if (existsOnServer) {
                                    return { ...acc, objectNodes: [...acc.objectNodes, objectUpdateToNode(object, currentState.mapModule.projectDataFields)] };
                                }
                                if (isAnalysisLayer(object.layerId)) {
                                    return { ...acc, analysisObjects: [...acc.analysisObjects, object] };
                                }
                                return acc;
                            },
                            { objectNodes: [], analysisObjects: [] }
                        );
                        if (objectNodes.length) {
                            treeBrowserContext.toggleSelectNodes(objectNodes, { from: FROM_MAP_UNDO_REDO, ignoreExistingSelection: true, undoable: false });
                        }
                        if (analysisObjects.length) {
                            eventStream.next({
                                type: machineEventTypes.selectObjects,
                                payload: { selectedMapObjectIds: analysisObjects, from: FROM_MAP_UNDO_REDO, updateStore: true },
                            });
                        }

                        // After an update, get the state again
                        currentState = rootStore.getState().mapReducer;

                        const remoteIds = currentState.mapModule.localToRemoteMapObjectIdsMap.value;

                        // If the object exists on the backend and the geometry is valid, send the update to the backend
                        const objectsToUpdateOnBackend = updatedMapObjects.filter(
                            (object) => isValidUuid(object.layerId) && remoteIds[object.objectId] !== undefined && isCompositionValid(object.geometry)
                        );
                        if (objectsToUpdateOnBackend.length) {
                            dispatch(uploadGeometry(objectsToUpdateOnBackend));

                            // Is there only one object selected and has it been moved
                            if (
                                currentState.mapModule.mapObjectsSelected.value.length === 1 &&
                                objectsToUpdateOnBackend.some((update) => update.objectId === currentState.mapModule.mapObjectsSelected.value[0].objectId)
                            ) {
                                // Then update the what3words value (if applicable)
                                getWhat3WordsValueForObjectUpdates(objectsToUpdateOnBackend, currentState.mapModule.layers.value, dispatch, true);
                            } else {
                                // Set all what3words values of updated objects to null
                                patchWhat3WordsValueToNullRemote(objectsToUpdateOnBackend, currentState.mapModule.layers.value, dispatch);
                            }

                            // If any system data field values have been updated, save it on the backend
                            const { areSystemDataFieldsUpdated } = updateMapObjectSystemDataFieldsResponse;
                            if (areSystemDataFieldsUpdated) {
                                dispatch(
                                    patchMapObjectsDataFieldValues({
                                        mapObjects: createMapObjectsWithSystemDataFields(objectsToUpdateOnBackend, currentState.mapModule.projectDataFields),
                                    })
                                );
                            }
                        }
                    }
                }
            })
            // If an undo/redo leads to a deletion, we need to handle that here.
            .onDelete((deletion) => {
                if (deletion.type === "mapObject") {
                    const deleteIds = deletion.items.map((item) => ({ objectId: item.objectId, layerId: item.layerId }));
                    const currentState = rootStore.getState().mapReducer;
                    const selectedMapObjects = currentState.mapModule.mapObjectsSelected.value;
                    const latestObjects = deleteIds.map(({ objectId, layerId }) =>
                        localGeoJsonObjectToObjectUpdate(currentState.mapModule.geoJSON.value[layerId]?.find((x) => x.objectId === objectId))
                    );

                    const deletionsSelected = deleteIds.filter(({ objectId }) => selectedMapObjects.some((selectedMapObject) => selectedMapObject.objectId === objectId));
                    // Go through the machine for the deletion. It makes more sense to send this directly to the machine since it can change the mode.
                    // Additionally, it makes it much easier to follow the state machine path of removing epehemeral geometries rather than getting the store involved.
                    if (deletionsSelected.length) {
                        eventStream.next({ type: machineEventTypes.deleteObject, payload: { updateStore: false } });
                    }

                    // Separate the deletions into analysis and object deletions
                    const { analysisIds, objectIds } = deleteIds.reduce<{ analysisIds: string[]; objectIds: SelectedMapObject[] }>(
                        (acc, { objectId, layerId }) =>
                            isAnalysisLayer(layerId) ? { ...acc, analysisIds: [...acc.analysisIds, objectId] } : { ...acc, objectIds: [...acc.objectIds, { objectId, layerId }] },
                        {
                            analysisIds: [],
                            objectIds: [],
                        }
                    );
                    if (objectIds.length) {
                        dispatch(deleteObjects({ mapObjects: objectIds, modifyStore: true }));
                        treeBrowserContext.removeNodesLocally(objectIds.map(({ objectId }) => objectId));
                    }
                    if (analysisIds.length) {
                        dispatch(map.actions.removeObjects(analysisIds));
                    }
                    return latestObjects;
                }
                return undefined;
            })
            // If an undo/redo leads to a restoration, we need to handle that here.
            .onRestore((creation) => {
                if (creation.type === "mapObject") {
                    // Separate the restorations into analysis and object restorations
                    const restoration = creation.items.reduce<{ objects: ObjectUpdate[]; analysis: ObjectUpdate[] }>(
                        (acc, item) => (isAnalysisLayer(item.layerId) ? { ...acc, analysis: [...acc.analysis, item] } : { ...acc, objects: [...acc.objects, item] }),
                        { objects: [], analysis: [] }
                    );
                    if (restoration.objects.length) {
                        dispatch(restoreObjects(restoration.objects));
                        const { projectDataFields } = rootStore.getState().mapReducer.mapModule;
                        const nodesToAdd = restoration.objects.map((creation) => objectUpdateToNode(creation, projectDataFields));
                        if (nodesToAdd.length > 0) {
                            treeBrowserContext.addNodesLocally(nodesToAdd);
                        }
                    }
                    if (restoration.analysis.length) {
                        dispatch(map.actions.softlySetLayerGeometry(restoration.analysis.map(mapObjectUpdateToLocalGeoJsonObject)));
                    }
                    eventStream.next({
                        type: machineEventTypes.selectObjects,
                        payload: { selectedMapObjectIds: creation.items.map(({ objectId, layerId }) => ({ objectId, layerId })), from: FROM_MAP_UNDO_REDO, updateStore: true },
                    });
                }
            });

        return {
            id: undoRedo.id,
            push: (entry) => {
                // If we are deselecting objects, we need to ensure that they exist in the undo stack (can occur when selecting another object or layer midway through composing an object)
                if (entry.type === "mapObjectSelection" && entry.operation === "update") {
                    const ids = entry.change.flatMap(({ from }) => from);
                    if (ids.length) {
                        const validObjects = getValidGeometriesFromGeoJson(geoJson.current, ids);
                        if (validObjects.length) {
                            pushMissingCompositions(validObjects, undoRedo, mode);
                        }
                        // Ensure we only register the deselcting of _valid_ objects
                        // eslint-disable-next-line no-param-reassign
                        entry.change = entry.change.map((change) => ({ ...change, from: change.from.filter(({ objectId }) => validObjects.some((i) => i.objectId === objectId)) }));
                    }
                }
                undoRedo.push(entry);
            },
            undo: undoRedo.undo.bind(undoRedo),
            redo: undoRedo.redo.bind(undoRedo),
            undoStack$: undoRedo.undoStack$,
            redoStack$: undoRedo.redoStack$,
            clear: undoRedo.clear.bind(undoRedo),
            removeFromStack: undoRedo.removeFromStack.bind(undoRedo),
            registerDelete: registerDelete(undoRedo, { mode, geoJson }).bind(undoRedo),
        };
    });

    // Can map undo/redo
    const canMapUndo = useObservableValue(mapUndoRedo.undoStack$, mapUndoRedo.undoStack$?.value)?.length > 0;
    const canMapRedo = useObservableValue(mapUndoRedo.redoStack$, mapUndoRedo.redoStack$?.value)?.length > 0;

    // Composition undo/redo (handled inside engine-generic)
    const compositionUndoRedo = useMemo(() => getCompositionUndoRedo?.(), [mode.current]);

    // Can composition undo/redo
    const canCompositionUndo = useObservableValue(compositionUndoRedo?.instance.undoStack$)?.length > 0;
    const canCompositionRedo = useObservableValue(compositionUndoRedo?.instance.redoStack$)?.length > 0;

    /** Merged undo/redo instances between map and composition. Uses composition undo/redo while in composition mode */
    const mergedUndoRedo: MapUndoRedoContextType = useMemo(() => {
        const result = {
            ...mapUndoRedo,
            // Use both map and composition undo/redo here because if there is no undo available for composition while we're in composition mode, we should still be able to undo if there is an undo in the main map undo stack
            canUndo: canMapUndo || canCompositionUndo,
            canRedo: canMapRedo || canCompositionRedo,
        };

        if (checkModeIsPresent(mode.current, "composition") && compositionUndoRedo != null) {
            return {
                ...result,
                canRedo: canCompositionRedo, // If we're in composition mode, we should only be able to redo if there is a redo available in the composition undo/redo stack
                id: compositionUndoRedo.instance.id,
                undo: compositionUndoRedo.undo.bind(compositionUndoRedo),
                redo: compositionUndoRedo.redo.bind(compositionUndoRedo),
            };
        }

        return {
            ...result,
            undo: () => {
                result.undo();
                incrementMonitoringCount({ name: MetricName.MAP_UNDO, resourceId: mapId });
            },
            redo: () => {
                result.redo();
                incrementMonitoringCount({ name: MetricName.MAP_REDO, resourceId: mapId });
            },
        };
    }, [mode.current, canMapUndo, canMapRedo, canCompositionUndo, canCompositionRedo, mapId]);

    useMapKeyShortcuts(mergedUndoRedo);

    useObservableCallback(mapUndoRedoEventStream.subject, {}, (value) => {
        mapUndoRedo.push({ type: value.type, operation: "update", change: [{ from: value.payload.oldSelection, to: value.payload.newSelection }] });
    });

    return (
        <MapUndoRedoContext.Provider value={mergedUndoRedo}>
            <UndoRedoContext.Provider value={mergedUndoRedo}>{children}</UndoRedoContext.Provider>
        </MapUndoRedoContext.Provider>
    );
};

/**
 * This function acts as a middleware to handle communication with the undo/redo stack on deletion of a map object(s).
 * When a map object(s) has been deleted, we must add this to the undo stack.
 * A deletion must include the full map object, so that if the user requests a redo, the full object is available.
 * In order to create a smooth experience for the user, in some cases we need to add a creation before the deletion.
 */
export function registerDelete(
    undoRedo: UndoRedoV2<MapUndoRedoItemPartial, MapUndoRedoItem, MapObjectUndoRedoEntry | MapObjectSelectionUndoRedoEntry>,
    state: { mode: { current: MapMode }; geoJson: { current: LocalGeoJson } }
) {
    return (ids: SelectedMapObject[]) => {
        const items = getValidGeometriesFromGeoJson(state.geoJson.current, ids);
        if (items.length) {
            pushMissingCompositions(items, undoRedo, state.mode);
            undoRedo.push({ type: "mapObject", operation: "delete", items });
        }
    };
}

/** Returns all the objects which are valid from the given array of ids */
const getValidGeometriesFromGeoJson = (geoJson: LocalGeoJson, ids: SelectedMapObject[]) =>
    ids.reduce<ExtractRestorableEntry<MapObjectUndoRedoEntry>["items"]>((acc, { objectId, layerId }) => {
        const objectGeoJson = geoJson[layerId]?.find((x) => x.objectId === objectId);
        if (objectGeoJson == null) {
            return acc;
        }
        const mapObject = localGeoJsonObjectToObjectUpdate(objectGeoJson);
        // Only register deletions of objects which have valid geometry
        const isValid = isCompositionValid(mapObject?.geometry);
        if (isValid) {
            return [...acc, mapObject];
        }
        return acc;
    }, []);

/** Checks the given undo stack for the presence of the given items. If they are missing, then we register their creation */
function pushMissingCompositions(
    objects: ObjectUpdate[],
    undoRedo: UndoRedoV2<MapUndoRedoItemPartial, MapUndoRedoItem, MapObjectUndoRedoEntry | MapObjectSelectionUndoRedoEntry>,
    mode: { current: MapMode }
) {
    // If we're in composition mode, there's a chance we're going to be deleting something that doesn't yet exist in the history
    if (checkModeIsPresent(mode.current, "composition")) {
        // Get any objects which are being deleted but not registered in the history
        const { missing } = objects.reduce(
            (acc, deletion) => {
                // Check if it was selected
                const undoableSelections = undoRedo.undoStack$.value.filter((e) => e.type === "mapObjectSelection");
                const lastUndoableSelection = undoableSelections[undoableSelections.length - 1];
                const isSelected =
                    lastUndoableSelection?.type === "mapObjectSelection" &&
                    lastUndoableSelection.operation === "update" &&
                    lastUndoableSelection.change.some(({ to }) => to.some(({ objectId }) => objectId === deletion.objectId));

                // Get the last object entry
                const undoableObjectEntries = undoRedo.undoStack$.value.filter((e) => e.type === "mapObject");
                const lastUndoableObjectEntry = undoableObjectEntries[undoableObjectEntries.length - 1];

                // check if it was created
                const isCreated =
                    lastUndoableObjectEntry?.type === "mapObject" &&
                    lastUndoableObjectEntry.operation === "create" &&
                    lastUndoableObjectEntry.items.some(({ objectId }) => objectId === deletion.objectId);

                // check if it was updated
                const isUpdated =
                    lastUndoableObjectEntry?.type === "mapObject" &&
                    lastUndoableObjectEntry.operation === "update" &&
                    lastUndoableObjectEntry.change.some(({ to }) => to.objectId === deletion.objectId);

                if (isSelected || isCreated || isUpdated) {
                    return acc;
                }
                return { ...acc, missing: [...acc.missing, deletion] };
            },
            { missing: [] }
        );
        // If any are missing from history, it's because they were newly created and the current undo stack doesn't know about them.
        // So, we mimic the beahivour of creating it before we delete it
        if (missing.length) {
            undoRedo.push({
                type: "mapObject",
                operation: "create",
                items: objects.filter((item) => missing.some(({ objectId }) => objectId === item.objectId)),
            });
        }
    }
}
